import produce from "immer";
import { snakeCase } from "lodash";
import * as React from "react";
import { Suspense } from "react";
import { saveOrgData } from "../../libs/organization";
import { unwrapUserContextData } from "../../libs/user_context";
import Breadcrumbs from "../core/Breadcrumbs";
import Button from "../core/Button";
import Card from "../core/Card";
import PageContainer from "../core/PageContainer";
import { IStep } from "../core/Stepper";
import BulkUserInvite from "../onboarding/BulkUserInvite";
import CompanyInfoGrab from "../onboarding/CompanyInfoGrab";
import DiscAssessment from "../onboarding/DiscAssessment";
import StrategySession from "../onboarding/StrategySession";
import HardwareSignup from "../onboarding/HardwareSignup";
import OnboardFinish from "../onboarding/OnboardFinish";
import { Spring } from "react-spring/renderprops";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import { normalizeApiResponse, normalizeRecord } from "../../libs/api_utils";

const OnboardingSettings = React.lazy(() =>
  import("../onboarding/OnboardingSettings")
);
const Stepper = React.lazy(() => import("../core/Stepper"));

interface IOnboardingStepDef extends IStep {
  propertyName?: keyof IOnboardingSteps;
}

interface IProps {
  userContext: IUserContextData;
  onboardData: IJsonApiResponse<IOnboardDataRecord>;
}

interface IState {
  userContext: IUserContextLegacy;
  org: IOrganizationRecord;
  currentStepIndex: number;
  allowNextStep: boolean;
  beforeNextStep?: (() => void | Promise<void>) | null;
}

class OnboardingPage extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const userContext = unwrapUserContextData(this.props.userContext);
    const org = userContext.org;

    const onboardStepsData = org.attributes.onboardingStepsData || {};
    const orgSteps = onboardStepsData.steps || {};

    let currentStepIndex = 0;

    // Array.some allows us to break out of the loop by returning true
    this.getOnboardSteps().some((s, i) => {
      currentStepIndex = i;
      if (!s.propertyName) {
        return true;
      }
      const stepData = orgSteps[s.propertyName];
      return !stepData || !stepData.complete;
    });

    this.state = { userContext, org, currentStepIndex, allowNextStep: true };
  }

  getOnboardSteps() {
    const onboardData = this.props.onboardData.data != null ?  normalizeApiResponse(this.props.onboardData) : {}
    const approved_steps = onboardData.hasOwnProperty('approved_steps') ? onboardData['approved_steps'] : null
    let all_steps = [
      { title: "Settings", propertyName: "settings", dbMatch:'settings' },
      { title: "Invites", propertyName: "bulkUserInvite", dbMatch:'invites' },
      { title: "Company Info", propertyName: "companyInfoGrab", dbMatch:'companyInfo' },
      { title: "Strategy Session", propertyName: "strategySessionScheduling", dbMatch:'strategySession' },
      { title: "DISC Assessment", propertyName: "discAssessment", dbMatch:'disc' },    
      { title: "Hardware", propertyName: "hardwareSignup", dbMatch:'hardware' },
      { title: "Done", dbMatch:'done' }
    ]

    let cur_steps = all_steps
    // add check for all orgs in progress who were onboarding but did not have approved_steps saved in DB yet
    if(approved_steps != null){
      cur_steps = all_steps.filter(s => approved_steps.includes(s.dbMatch))
    }
    
  
   return cur_steps
  }

  updateAllowNextStep = (allowNextStep: boolean) =>
    this.setState({ allowNextStep });

  onBeforeNextStep = (callback: () => void | Promise<void>) =>
    this.setState({ beforeNextStep: callback });

  saveOrg = async () => {
    const { org } = this.state;
    const response = await saveOrgData(org);
  };

  getStepComponent = (stepIndex: number) => {
    const { org } = this.state;
    const step = this.getOnboardSteps()[stepIndex];

    const onboardStepsData = org.attributes.onboardingStepsData || {};
    const orgSteps = onboardStepsData.steps || {};

    const commonProps = {
      onBeforeNextStep: this.onBeforeNextStep,
      allowNextStep: this.updateAllowNextStep,
      org,
      stepData: step.propertyName && orgSteps[step.propertyName],
      setStepComplete: (complete = true) =>
        this.setState(
          produce<IState>(draft => {
            if (!step.propertyName) {
              return;
            }

            if (!draft.org.attributes.onboardingStepsData) {
              draft.org.attributes.onboardingStepsData = {};
            }

            if (!draft.org.attributes.onboardingStepsData.steps) {
              draft.org.attributes.onboardingStepsData.steps = {};
            }

            const stepData =
              draft.org.attributes.onboardingStepsData.steps[
                step.propertyName
              ] || {};

            stepData.complete = complete;
            draft.org.attributes.onboardingStepsData.steps[
              step.propertyName
            ] = stepData;
          })
        ),
    };

    let component: React.ReactNode;
    switch (snakeCase(step.title).toUpperCase()) {
      case "SETTINGS":
        component = (
          <Suspense fallback={<div>Loading...</div>}>
            <OnboardingSettings
              updateSettings={(settings: IOrganizationSettings) =>
                this.setState(
                  produce<IState>(draft => {
                    draft.org.attributes.settings = settings;
                  })
                )
              }
              {...commonProps}
            />
          </Suspense>
        );
        break;
      case "INVITES":
        component = <BulkUserInvite {...commonProps} />;
        break;
      case "COMPANY_INFO":
        component = <CompanyInfoGrab {...commonProps} />;
        break;
      case "STRATEGY_SESSION":
        component = <StrategySession {...commonProps} />;
        break;
      case "DISC_ASSESSMENT":
        component = <DiscAssessment {...commonProps} />;
        break;
      case "HARDWARE":
        component = <HardwareSignup {...commonProps} />;
        break;
      case "DONE":
        component = (
          <OnboardFinish
            setWizardComplete={() => {
              this.setState(
                produce<IState>(draft => {
                  if (!draft.org.attributes.onboardingStepsData) {
                    draft.org.attributes.onboardingStepsData = {};
                  }
                  draft.org.attributes.onboardingStepsData.dontReturnOnLogin = true;
                }),
                this.saveOrg
              );
            }}
            {...commonProps}
          />
        );
        break;
      default:
        component = <div />;
    }

    return (
      <Spring from={{ opacity: 0 }} to={{ opacity: 1 }} reset={true}>
        {props => <div style={props}>{component}</div>}
      </Spring>
    );
  };

  nextStep = async () => {
    if (this.state.beforeNextStep) {
      await this.state.beforeNextStep();
    }

    this.setState(
      produce<IState>(draft => {
        draft.currentStepIndex += 1;
        draft.beforeNextStep = null;
      }),
      this.saveOrg
    );
  };

  prevStep = () =>
    this.setState(
      {
        // allowNextStep: false,
        currentStepIndex: this.state.currentStepIndex - 1,
      },
      this.saveOrg
    );

  render() {
    const { currentStepIndex, allowNextStep } = this.state;

    const hasNextStep = currentStepIndex + 1 < this.getOnboardSteps().length;
    const canNextStep = hasNextStep && allowNextStep;
    const canPrevStep = currentStepIndex - 1 >= 0;

    const stepperSteps = this.getOnboardSteps().map((s, i) => ({
      ...s,
      onClick: () => this.setState({ currentStepIndex: i }, this.saveOrg),
    }));

    return (
      <PageContainer>
        <Breadcrumbs crumbs={[{ label: "Welcome to CharTec CORE!" }]} />
        <Card title={"Onboarding Wizard"}>
          {this.getStepComponent(currentStepIndex)}
          <hr />
          <div className="level">
            <div className="level-left">
              {canPrevStep && (
                <div className="level-item">
                  <Button onClick={this.prevStep}>
                    <i className={"fa fa-chevron-left"} /> Previous Step
                  </Button>
                </div>
              )}
            </div>
            <div className="level-right">
              <div className="level-item">
                {hasNextStep ? (
                  <Button onClick={this.nextStep} disabled={!canNextStep}>
                    Next Step <i className={"fa fa-chevron-right"} />
                  </Button>
                ) : (
                  <Button onClick={() => Turbolinks.visit("/")}>Go Home</Button>
                )}
              </div>
            </div>
          </div>
          <Suspense fallback={null}>
            <Stepper
              steps={stepperSteps}
              activeStep={currentStepIndex}
              allowClickingUncompleted={true}
            />
          </Suspense>
        </Card>
      </PageContainer>
    );
  }
}

export default wrapComponentForErrors(OnboardingPage);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/OnboardingPage.tsx